import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, Loader2, Eye } from "lucide-react";
import { createVisitor, getVisitor, updateVisitor } from "@/lib/api";
import FileUploadField from "@/components/FileUploadField";

const EMPTY = {
  nama_tamu: "", jabatan_tamu: "", jumlah_tamu: 1,
  anggota_tamu: [],
  nama_pendamping: "", jabatan_pendamping: "", jumlah_pendamping: 0,
  daftar_pendamping: [],
  instansi_asal: "", daerah_asal: "",
  rencana_kunjungan: "", tanggal_kunjungan: "", waktu_kunjungan: "",
  perihal_kunjungan: "", penanggung_jawab: "", whatsapp_penanggung_jawab: "",
  whatsapp_tamu: "",
  nomor_surat_kunjungan: "", nomor_surat_tugas: "",
  surat_kunjungan_file: null, surat_tugas_file: null,
};

const REQUIRED = [
  "nama_tamu", "jabatan_tamu", "instansi_asal", "daerah_asal",
  "rencana_kunjungan", "tanggal_kunjungan", "waktu_kunjungan",
  "perihal_kunjungan", "penanggung_jawab",
  "nomor_surat_kunjungan", "nomor_surat_tugas",
];

function Field({ label, name, type = "text", required, value, onChange, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor={name}>
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <Input
        id={name}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`field-${name}`}
        className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
        {...rest}
      />
    </div>
  );
}

export default function VisitorForm({ publicMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("form"); // form | preview | success
  const isEdit = Boolean(id);

  useEffect(() => {
    if (id) {
      getVisitor(id).then((d) => setForm({ ...EMPTY, ...d, anggota_tamu: d.anggota_tamu || [], daftar_pendamping: d.daftar_pendamping || [] }))
        .catch(() => toast.error("Data tidak ditemukan"));
    }
  }, [id]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-resize anggota_tamu to (jumlah_tamu - 1)
  useEffect(() => {
    const n = Math.max(0, (Number(form.jumlah_tamu) || 1) - 1);
    const curr = form.anggota_tamu || [];
    if (curr.length === n) return;
    const next = Array.from({ length: n }, (_, i) => curr[i] || { nama: "", jabatan: "" });
    setForm(f => ({ ...f, anggota_tamu: next }));
     
  }, [form.jumlah_tamu]);

  // Auto-resize daftar_pendamping to jumlah_pendamping
  useEffect(() => {
    const n = Math.max(0, Number(form.jumlah_pendamping) || 0);
    const curr = form.daftar_pendamping || [];
    if (curr.length === n) return;
    const next = Array.from({ length: n }, (_, i) => curr[i] || { nama: "", jabatan: "" });
    setForm(f => ({ ...f, daftar_pendamping: next }));
     
  }, [form.jumlah_pendamping]);

  const updateMember = (listKey, idx, field, val) => {
    setForm(f => {
      const list = [...(f[listKey] || [])];
      list[idx] = { ...(list[idx] || {}), [field]: val };
      return { ...f, [listKey]: list };
    });
  };

  const goPreview = (e) => {
    e.preventDefault();
    const required = publicMode
      ? ["nama_tamu", "jabatan_tamu", "instansi_asal", "tanggal_kunjungan", "perihal_kunjungan"]
      : REQUIRED;
    for (const key of required) {
      if (!String(form[key] ?? "").trim()) {
        toast.error("Mohon lengkapi semua kolom bertanda *");
        return;
      }
    }
    if (publicMode) { setStep("preview"); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else submit();
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        jumlah_tamu: Number(form.jumlah_tamu) || 1,
        jumlah_pendamping: Number(form.jumlah_pendamping) || 0,
        source: publicMode ? "registrasi_public" : "registrasi",
      };
      const result = isEdit ? await updateVisitor(id, payload) : await createVisitor(payload);
      toast.success(isEdit ? "Data kunjungan diperbarui" : "Tamu berhasil terdaftar");
      if (publicMode) { setStep("success"); window.scrollTo({ top: 0, behavior: "smooth" }); }
      else navigate(`/admin/tamu`, { state: { highlight: result.id } });
    } catch {
      toast.error("Gagal menyimpan data");
    } finally {
      setSaving(false);
    }
  };

  const f = (name) => ({ name, value: form[name], onChange: update });

  // Success screen (public mode)
  if (publicMode && step === "success") {
    return (
      <div className="max-w-3xl mx-auto">
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-8 text-center">
            <div className="inline-flex w-16 h-16 rounded-full bg-emerald-500 text-white items-center justify-center shadow-md text-3xl font-bold">✓</div>
            <h2 className="mt-4 text-2xl font-bold text-slate-900">Registrasi Terkirim</h2>
            <p className="text-slate-600 mt-1">Data kunjungan Anda telah diterima oleh Sekretariat DPRD Kabupaten Seruyan.</p>
            <div className="mt-5 flex justify-center gap-2">
              <Button onClick={() => { setForm(EMPTY); setStep("form"); }} className="btn-primary" data-testid="form-new-entry">
                Registrasi Lain
              </Button>
              <Button variant="outline" asChild><Link to="/buku-tamu">Lihat Buku Tamu</Link></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Preview step (public mode)
  if (publicMode && step === "preview") {
    const Row = ({ label, value }) => (
      <div className="flex gap-3 py-2 border-b border-slate-100 last:border-0">
        <div className="w-48 text-xs uppercase tracking-wider font-semibold text-slate-500 pt-0.5">{label}</div>
        <div className="flex-1 text-sm text-slate-900 font-medium whitespace-pre-wrap">{value || "—"}</div>
      </div>
    );
    const anggotaStr = (form.anggota_tamu || []).filter(a => a?.nama).map(a => `${a.nama}${a.jabatan ? " — " + a.jabatan : ""}`).join("\n");
    const pdmpStr = (form.daftar_pendamping || []).filter(a => a?.nama).map(a => `${a.nama}${a.jabatan ? " — " + a.jabatan : ""}`).join("\n");
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Ringkasan Registrasi</h1>
          <p className="text-slate-600 text-sm mt-1">Periksa data berikut sebelum dikirim.</p>
        </div>
        <Card className="border-[#C59B27] border-2 shadow-lg">
          <CardContent className="p-6">
            <div className="rounded-md bg-slate-50/70 border border-slate-200 px-5 py-3">
              <Row label="Nama Tamu" value={form.nama_tamu} />
              <Row label="Jabatan Tamu" value={form.jabatan_tamu} />
              <Row label="Jumlah Tamu" value={`${form.jumlah_tamu} orang`} />
              {anggotaStr && <Row label="Anggota Tamu" value={anggotaStr} />}
              <Row label="Instansi" value={form.instansi_asal} />
              <Row label="WhatsApp Tamu" value={form.whatsapp_tamu} />
              <Row label="Daerah Asal" value={form.daerah_asal} />
              <Row label="Jumlah Pendamping" value={`${form.jumlah_pendamping} orang`} />
              {pdmpStr && <Row label="Daftar Pendamping" value={pdmpStr} />}
              <Row label="Tanggal Kunjungan" value={form.tanggal_kunjungan} />
              <Row label="Waktu" value={form.waktu_kunjungan} />
              <Row label="Rencana / Agenda" value={form.rencana_kunjungan} />
              <Row label="Perihal / Tujuan" value={form.perihal_kunjungan} />
              <Row label="Penanggung Jawab" value={form.penanggung_jawab} />
              <Row label="WhatsApp Narahubung" value={form.whatsapp_penanggung_jawab} />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" disabled={saving} onClick={() => setStep("form")} data-testid="form-edit-button">
                Kembali Edit
              </Button>
              <Button onClick={submit} disabled={saving} className="btn-primary" data-testid="form-confirm-submit">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Kirim Registrasi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <div>
          {!publicMode && (
            <Button asChild variant="ghost" size="sm" data-testid="form-back-button">
              <Link to="/admin/tamu"><ArrowLeft className="w-4 h-4 mr-1" /> Kembali</Link>
            </Button>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
            {isEdit ? "Perbarui Data Kunjungan" : "Registrasi Tamu Kunjungan"}
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            {publicMode
              ? "Isi data kunjungan Anda. Sebelum dikirim, Anda akan diminta memeriksa ringkasan terlebih dahulu."
              : "Isi informasi tamu beserta dokumen pendukung kunjungan resmi."}
          </p>
        </div>
      </div>

      <form onSubmit={goPreview} className="space-y-6">
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">1. Data Tamu Utama</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Nama Tamu / Ketua Rombongan" {...f("nama_tamu")} required />
            <Field label="Jabatan Tamu" {...f("jabatan_tamu")} required />
            <Field label="Jumlah Anggota Tamu (Orang)" {...f("jumlah_tamu")} type="number" min="1" required />
            <Field label="Nama Instansi Asal" {...f("instansi_asal")} required />
            <Field label="Nomor WhatsApp Tamu" {...f("whatsapp_tamu")} type="tel" placeholder="08xxxxxxxxxx" />
            <div className="md:col-span-2">
              <Field label="Daerah Asal (Kota/Kabupaten/Provinsi)" {...f("daerah_asal")} required />
            </div>

            {(form.anggota_tamu?.length > 0) && (
              <div className="md:col-span-2 space-y-3">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#0B2545] border-l-4 border-[#C59B27] pl-2">
                  Anggota Tamu Lainnya ({form.anggota_tamu.length} orang)
                </div>
                {form.anggota_tamu.map((it, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr] gap-3 items-end bg-slate-50/60 border border-slate-200 rounded-md p-3">
                    <div className="text-xs font-semibold text-slate-500 md:pb-2.5">Anggota #{i + 2}</div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Nama</Label>
                      <Input
                        value={it.nama || ""}
                        onChange={(e) => updateMember("anggota_tamu", i, "nama", e.target.value)}
                        className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
                        data-testid={`anggota-nama-${i}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Jabatan</Label>
                      <Input
                        value={it.jabatan || ""}
                        onChange={(e) => updateMember("anggota_tamu", i, "jabatan", e.target.value)}
                        className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
                        data-testid={`anggota-jabatan-${i}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">2. Data Pendamping</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Jabatan Pendamping (umum, opsional)" {...f("jabatan_pendamping")} />
            <Field label="Jumlah Pendamping (Orang)" {...f("jumlah_pendamping")} type="number" min="0" />

            {(form.daftar_pendamping?.length > 0) ? (
              <div className="md:col-span-2 space-y-3">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#0B2545] border-l-4 border-[#C59B27] pl-2">
                  Daftar Pendamping ({form.daftar_pendamping.length} orang)
                </div>
                {form.daftar_pendamping.map((it, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr] gap-3 items-end bg-slate-50/60 border border-slate-200 rounded-md p-3">
                    <div className="text-xs font-semibold text-slate-500 md:pb-2.5">Pendamping #{i + 1}</div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Nama</Label>
                      <Input
                        value={it.nama || ""}
                        onChange={(e) => updateMember("daftar_pendamping", i, "nama", e.target.value)}
                        className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
                        data-testid={`pendamping-nama-${i}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Jabatan</Label>
                      <Input
                        value={it.jabatan || ""}
                        onChange={(e) => updateMember("daftar_pendamping", i, "jabatan", e.target.value)}
                        className="bg-white border-slate-300 focus-visible:ring-[#0B2545]"
                        data-testid={`pendamping-jabatan-${i}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="md:col-span-2 text-xs text-slate-500 italic">
                Isi "Jumlah Pendamping" untuk memunculkan isian nama & jabatan per pendamping.
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">3. Detail Kunjungan</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Hari & Tanggal Kunjungan" {...f("tanggal_kunjungan")} type="date" required />
            <Field label="Waktu Kunjungan (WIB)" {...f("waktu_kunjungan")} type="time" required />
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                Rencana / Agenda Kunjungan <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={form.rencana_kunjungan}
                onChange={(e) => update("rencana_kunjungan", e.target.value)}
                className="min-h-20 bg-white border-slate-300 focus-visible:ring-[#0B2545]"
                data-testid="field-rencana_kunjungan"
              />
            </div>
            <div className="md:col-span-2">
              <Field label="Perihal / Maksud Kunjungan" {...f("perihal_kunjungan")} required />
            </div>
            <Field label="Penanggung Jawab / Narahubung" {...f("penanggung_jawab")} required />
            <Field label="Nomor WhatsApp Narahubung" {...f("whatsapp_penanggung_jawab")} type="tel" placeholder="08xxxxxxxxxx" />
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-base font-semibold text-slate-800">4. Dokumen Pendukung</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={`Nomor Surat Kunjungan / Permohonan${publicMode ? "" : ""}`} {...f("nomor_surat_kunjungan")} required={!publicMode} placeholder="contoh: 005/123/DPRD/2026" />
            <Field label="Nomor Surat Tugas" {...f("nomor_surat_tugas")} required={!publicMode} placeholder="contoh: 094/045/DPRD/2026" />
            <FileUploadField
              label="Scan Surat Kunjungan (opsional)"
              value={form.surat_kunjungan_file}
              onChange={(v) => update("surat_kunjungan_file", v)}
              testid="upload-surat-kunjungan"
            />
            <FileUploadField
              label="Scan Surat Tugas (opsional)"
              value={form.surat_tugas_file}
              onChange={(v) => update("surat_tugas_file", v)}
              testid="upload-surat-tugas"
            />
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3 pt-2">
          {!publicMode && (
            <Button asChild variant="outline" type="button">
              <Link to="/admin/tamu">Batal</Link>
            </Button>
          )}
          <Button type="submit" disabled={saving} className="btn-primary" data-testid="visitor-form-submit-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (publicMode ? <Eye className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />)}
            {publicMode ? "Lihat Ringkasan" : (isEdit ? "Simpan Perubahan" : "Daftarkan Tamu")}
          </Button>
        </div>
      </form>
    </div>
  );
}
